import React, { useState, useMemo } from 'react';
import { ArrowRight, FileText, Copy, Check, RotateCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatAnalysisText } from '@/lib/textFormatting';
import { toast } from '@/hooks/use-toast';
import { findMatchingBooks } from '@/lib/partnerBooks';
import type { HitResult } from '@/lib/types';

function isPdfUrl(url: string): boolean {
  return url.toLowerCase().endsWith('.pdf');
}

// Extract filename from PDF URL
function getPdfFilename(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const filename = pathname.split('/').pop() || '';
    // Remove .pdf extension and decode URL encoding
    const name = decodeURIComponent(filename.replace(/\.pdf$/i, ''));
    // Replace underscores/hyphens with spaces and capitalize first letter
    return name
      .replace(/[_-]/g, ' ')
      .replace(/^\w/, c => c.toUpperCase()) || 'Document PDF';
  } catch {
    return 'Document PDF';
  }
}

interface ResultCardProps {
  answer?: string;
  hits?: HitResult[];
  actionType?: 'find' | 'sumup' | 'analyze';
  cost?: number;
  onRegenerate?: () => void;
  question?: string;
  isLoading?: boolean;
}

const cardStyle = 'bg-primary/10 text-primary border-primary/20 sm:hover:bg-primary/20';
const partnerCardStyle = 'bg-partner text-partner-foreground border-partner/30 sm:hover:bg-partner-hover';

function getSource(url: string): string {
  if (url.includes('citations.institut-iliade.com')) return 'Citatio';
  if (url.includes('archives.polemia.com')) return 'Archives Polémia';
  if (url.includes('ojim.fr')) return 'OJIM';
  if (url.includes('polemia.com')) return 'Polémia';
  if (url.includes('bernardlugan.blogspot.com')) return 'Bernard Lugan';
  if (url.includes('observatoire-justice.org')) return 'ODJ';
  if (url.includes('observatoire-immigration.fr')) return 'OID';
  if (url.includes('observatoire-violences.fr')) return 'Observatoire des violences politiques';
  if (url.includes('marc-vanguard.com')) return 'Marc Vanguard';
  if (url.includes('boutique.institut-iliade.com')) return "Boutique de l'Institut Iliade";
  if (url.includes('institut-iliade.com')) return 'Institut Iliade';
  if (url.includes('metapoinfos.hautetfort.com')) return 'Métapo Infos';
  if (url.includes('revue-elements.com')) return 'Éléments';
  if (url.includes('contribuablesassocies.org')) return 'Contribuables Associés';
  if (url.includes('institutpourlajustice.org')) return 'Institut pour la justice';
  if (url.includes('libertepolitique.com')) return 'Liberté politique';
  if (url.includes('ifrap.org')) return 'Fondation Ifrap';
  if (url.includes('maximetandonnet.wordpress.com')) return 'Maxime Tandonnet';
  if (url.includes('georgeslebreton.substack.com')) return 'Georges Le Breton';
  if (url.includes('lignedroite.substack.com')) return 'Ligne Droite';
  if (url.includes('islamisation.fr')) return "Observatoire de l'islamisation";
  return 'Source';
}

// Common French words that need accent restoration
const accentMap: Record<string, string> = {
  'francais': 'Français', 'francaise': 'Française', 'francaises': 'Françaises',
  'francois': 'François', 'francoise': 'Françoise',
  'detestent': 'détestent', 'deteste': 'déteste', 'detester': 'détester',
  'etrange': 'étrange', 'etranger': 'étranger', 'etrangere': 'étrangère', 'etrangers': 'étrangers',
  'ete': 'été', 'etes': 'êtes', 'etre': 'être', 'etait': 'était', 'etaient': 'étaient',
  'etat': 'État', 'etats': 'États',
  'ecole': 'école', 'ecoles': 'écoles',
  'economie': 'économie', 'economique': 'économique', 'economiques': 'économiques',
  'ecologie': 'écologie', 'ecologique': 'écologique',
  'election': 'élection', 'elections': 'élections', 'electoral': 'électoral', 'electorale': 'électorale',
  'elus': 'élus', 'elu': 'élu', 'elue': 'élue',
  'energie': 'énergie', 'energies': 'énergies', 'energetique': 'énergétique',
  'epoque': 'époque', 'epoques': 'époques',
  'equipe': 'équipe', 'equipes': 'équipes',
  'evenement': 'événement', 'evenements': 'événements',
  'education': 'éducation', 'educatif': 'éducatif',
  'egalite': 'égalité', 'egal': 'égal', 'egaux': 'égaux',
  'eglise': 'Église', 'eglises': 'églises',
  'heroi': 'héroï', 'heros': 'héros',
  'democratie': 'démocratie', 'democrate': 'démocrate', 'democratique': 'démocratique',
  'defense': 'défense', 'defendre': 'défendre',
  'declin': 'déclin', 'decliner': 'décliner',
  'decision': 'décision', 'decisions': 'décisions', 'decider': 'décider',
  'demographie': 'démographie', 'demographique': 'démographique',
  'depart': 'départ', 'departement': 'département',
  'depute': 'député', 'deputes': 'députés', 'deputee': 'députée',
  'desastre': 'désastre', 'desastreux': 'désastreux',
  'developpement': 'développement', 'developper': 'développer',
  'differend': 'différend', 'different': 'différent', 'differente': 'différente', 'difference': 'différence',
  'difficulte': 'difficulté', 'difficile': 'difficile',
  'securite': 'sécurité', 'securitaire': 'sécuritaire',
  'societe': 'société', 'societes': 'sociétés',
  'siecle': 'siècle', 'siecles': 'siècles',
  'systeme': 'système', 'systemes': 'systèmes', 'systemique': 'systémique',
  'verite': 'vérité', 'verites': 'vérités', 'veritable': 'véritable',
  'generation': 'génération', 'generations': 'générations', 'general': 'général', 'generale': 'générale',
  'geometrie': 'géométrie',
  'geopolitique': 'géopolitique', 'geopolitiques': 'géopolitiques',
  'grece': 'Grèce', 'grec': 'grec', 'grecque': 'grecque',
  'heritage': 'héritage', 'heritier': 'héritier', 'heritiers': 'héritiers',
  'histoire': 'histoire', 'historique': 'historique',
  'hopital': 'hôpital', 'hopitaux': 'hôpitaux',
  'identite': 'identité', 'identitaire': 'identitaire',
  'ideologie': 'idéologie', 'ideologique': 'idéologique',
  'immigre': 'immigré', 'immigres': 'immigrés', 'immigree': 'immigrée', 'immigrees': 'immigrées',
  'immigration': 'immigration',
  'independance': 'indépendance', 'independant': 'indépendant',
  'inegalite': 'inégalité', 'inegalites': 'inégalités',
  'integrite': 'intégrité', 'integration': 'intégration',
  'interet': 'intérêt', 'interets': 'intérêts',
  'liberte': 'liberté', 'libertes': 'libertés',
  'litterature': 'littérature', 'litteraire': 'littéraire',
  'majorite': 'majorité',
  'memoire': 'mémoire', 'memoires': 'mémoires', 'memorial': 'mémorial',
  'medias': 'médias', 'media': 'média', 'mediatique': 'médiatique',
  'mediterranee': 'Méditerranée', 'mediterraneen': 'méditerranéen',
  'metier': 'métier', 'metiers': 'métiers',
  'methode': 'méthode', 'methodes': 'méthodes',
  'minorite': 'minorité', 'minorites': 'minorités',
  'modernite': 'modernité',
  'nationalite': 'nationalité',
  'necessite': 'nécessité', 'necessaire': 'nécessaire',
  'negociation': 'négociation', 'negociations': 'négociations',
  'parite': 'parité',
  'periode': 'période', 'periodes': 'périodes',
  'phenomene': 'phénomène', 'phenomenes': 'phénomènes',
  'piege': 'piège', 'pieges': 'pièges',
  'preference': 'préférence', 'preferences': 'préférences', 'preferer': 'préférer',
  'prejuge': 'préjugé', 'prejuges': 'préjugés',
  'president': 'président', 'presidente': 'présidente', 'presidentiel': 'présidentiel', 'presidentielle': 'présidentielle',
  'probleme': 'problème', 'problemes': 'problèmes', 'problematique': 'problématique',
  'proces': 'procès',
  'progres': 'progrès', 'progressiste': 'progressiste',
  'propriete': 'propriété', 'proprietaire': 'propriétaire',
  'publicite': 'publicité',
  'qualite': 'qualité', 'qualites': 'qualités',
  'realite': 'réalité', 'realites': 'réalités', 'reel': 'réel', 'reelle': 'réelle',
  'reforme': 'réforme', 'reformes': 'réformes', 'reformer': 'réformer',
  'refuge': 'réfugié', 'refugie': 'réfugié', 'refugies': 'réfugiés', 'refugiee': 'réfugiée',
  'region': 'région', 'regions': 'régions', 'regional': 'régional', 'regionale': 'régionale',
  'regime': 'régime', 'regimes': 'régimes',
  'remuneration': 'rémunération',
  'reponse': 'réponse', 'reponses': 'réponses', 'repondre': 'répondre',
  'republique': 'République', 'republicain': 'républicain', 'republicaine': 'républicaine',
  'resistance': 'résistance', 'resistant': 'résistant',
  'resultat': 'résultat', 'resultats': 'résultats',
  'reunion': 'réunion', 'reunions': 'réunions',
  'reussite': 'réussite', 'reussir': 'réussir',
  'revolution': 'révolution', 'revolutions': 'révolutions', 'revolutionnaire': 'révolutionnaire',
  'strategie': 'stratégie', 'strategies': 'stratégies', 'strategique': 'stratégique',
  'suprematie': 'suprématie',
  'telecommunication': 'télécommunication', 'television': 'télévision', 'tele': 'télé',
  'theorie': 'théorie', 'theories': 'théories', 'theorique': 'théorique',
  'tolerance': 'tolérance', 'tolerant': 'tolérant',
  'tragedie': 'tragédie', 'tragique': 'tragique',
  'traite': 'traité', 'traites': 'traités',
  'universite': 'université', 'universites': 'universités', 'universitaire': 'universitaire',
  'vecu': 'vécu',
  'vehicule': 'véhicule', 'vehicules': 'véhicules',
  'celebre': 'célèbre', 'celebres': 'célèbres', 'celebrer': 'célébrer', 'celebration': 'célébration',
  'cinema': 'cinéma', 'cinematographique': 'cinématographique',
  'cooperation': 'coopération', 'cooperer': 'coopérer',
  'creation': 'création', 'createur': 'créateur', 'creer': 'créer',
  'criminalite': 'criminalité',
  'delinquance': 'délinquance', 'delinquant': 'délinquant',
  'enquete': 'enquête', 'enquetes': 'enquêtes',
  'laicite': 'laïcité', 'laic': 'laïc', 'laique': 'laïque',
  'noel': 'Noël',
  'ou': 'où',
  'deja': 'déjà',
  'apres': 'après',
  'tres': 'très',
  'premiere': 'première', 'premier': 'premier', 'premiers': 'premiers', 'premieres': 'premières',
  'derniere': 'dernière', 'dernier': 'dernier', 'derniers': 'derniers', 'dernieres': 'dernières',
  'annee': 'année', 'annees': 'années',
  'a': 'à',
};

// Patterns that should keep their hyphen (French verb inversions)
const hyphenPatterns = [
  /\b(\w+)\s(t)\s(il|elle|on|ils|elles)\b/gi,  // "detestent t ils" → "détestent-t-ils"
  /\b(\w+)\s(il|elle|on|ils|elles|nous|vous|je|tu|ce|ci|la|le|les|meme|memes)\b/gi, // "sont ils" → "sont-ils"
];

// Words that should always be capitalized (proper nouns)
const alwaysCapitalized = new Set([
  'europe', 'occident', 'orient', 'france', 'allemagne', 'italie', 'espagne',
  'russie', 'chine', 'amerique', 'afrique', 'asie', 'mediterranee'
]);

// Fix French title: restore accents, hyphens, and apostrophes
function fixFrenchTitle(title: string): string {
  let fixed = title;
  
  // First, restore apostrophes for elision (l/d/n/s/j/m/t/c/qu followed by vowel)
  // "l europe" → "l'europe", "d abord" → "d'abord", "n importe" → "n'importe"
  fixed = fixed.replace(/\b([ldnjmtcs]|qu)\s+([aeiouhéèêëàâäùûüôöîï])/gi, "$1'$2");
  
  // Restore hyphens for verb inversions
  // Handle "verbe t il/elle/on" pattern (e.g., "detestent t ils" → "détestent-t-ils")
  fixed = fixed.replace(/\b(\w+)\s+t\s+(il|elle|on|ils|elles)\b/gi, '$1-t-$2');
  
  // Handle direct inversions (e.g., "sont ils" → "sont-ils", "est ce" → "est-ce")
  fixed = fixed.replace(/\b(est|sont|ont|fait|peut|veut|doit|va|a|avez|avons|sommes|etes|font|vont|peuvent|veulent|doivent)\s+(il|elle|on|ils|elles|ce)\b/gi, '$1-$2');
  
  // Handle "celui ci", "celle la", etc.
  fixed = fixed.replace(/\b(celui|celle|ceux|celles)\s+(ci|la)\b/gi, '$1-$2');
  
  // Handle "lui meme", "eux memes", etc.
  fixed = fixed.replace(/\b(lui|elle|eux|elles|nous|vous|moi|toi|soi)\s+(meme|memes)\b/gi, '$1-$2');
  
  // Now restore accents word by word
  const words = fixed.split(/(\s+|-|')/); // Split by spaces, hyphens and apostrophes, keeping delimiters
  const result = words.map(word => {
    if (word.match(/^\s+$/) || word === '-' || word === "'") return word; // Keep delimiters as-is
    
    const lowerWord = word.toLowerCase();
    
    // Check for words that should always be capitalized
    if (alwaysCapitalized.has(lowerWord)) {
      const base = accentMap[lowerWord] || word;
      return base[0].toUpperCase() + base.slice(1).toLowerCase();
    }
    
    const replacement = accentMap[lowerWord];
    
    if (replacement) {
      // Preserve original capitalization pattern
      if (word[0] === word[0].toUpperCase()) {
        return replacement[0].toUpperCase() + replacement.slice(1);
      }
      return replacement.toLowerCase();
    }
    return word;
  });
  
  return result.join('');
}

// Extract title from URL slug when API doesn't provide one
function extractTitleFromUrl(url: string): string | null {
  // Métapo Infos: extract title from slug (handles multiple URL patterns)
  // Examples: /archive/.../slug-123456.html OR /archive/.../slug.html
  if (url.includes('metapoinfos.hautetfort.com')) {
    // Try pattern with ID first: slug-NUMBER.html
    let match = url.match(/\/([^\/]+)-\d+\.html$/);
    if (!match) {
      // Fallback: slug.html (without trailing ID)
      match = url.match(/\/([^\/]+)\.html$/);
    }
    if (match) {
      const slug = match[1];
      return slug
        .replace(/-/g, ' ')
        .replace(/^\w/, c => c.toUpperCase());
    }
  }
  
  // Observatoire de l'immigration: extract title from slug (e.g., /les-francais-et-limmigration/)
  if (url.includes('observatoire-immigration.fr')) {
    const match = url.match(/\/([^\/]+)\/?$/);
    if (match && match[1] && match[1] !== '') {
      const slug = match[1];
      return slug
        .replace(/-/g, ' ')
        .replace(/^\w/, c => c.toUpperCase());
    }
  }
  
  // Archives Polémia: extract article ID (e.g., article.php?id=5448 or article-5201.html)
  if (url.includes('archives.polemia.com')) {
    const idMatch = url.match(/id=(\d+)|article-(\d+)/);
    if (idMatch) {
      const id = idMatch[1] || idMatch[2];
      return `Article #${id}`;
    }
  }
  
  return null;
}

// Check if title is a generic/useless title that should be replaced
function isGenericTitle(title: string, url: string): boolean {
  const genericTitles = [
    'métapo infos',
    'metapo infos',
    'métapoinfos',
    'metapoinfos',
    'document',
    'pdf',
    'accueil',
    'home',
  ];
  const lowerTitle = title.toLowerCase().trim();
  
  // Check against known generic titles
  if (genericTitles.some(g => lowerTitle.includes(g))) return true;
  
  // Check if title matches the source name
  const source = getSource(url).toLowerCase();
  if (lowerTitle === source || lowerTitle.includes(source)) return true;
  
  // For Métapo Infos, always prefer URL extraction (API titles are often unreliable)
  if (url.includes('metapoinfos.hautetfort.com')) return true;
  
  return false;
}

// Add tracking parameter to source URLs
function addTrackingParam(url: string): string {
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.set('ia-polemia', '');
    // Remove the trailing '=' that gets added for empty params
    return urlObj.toString().replace('ia-polemia=', 'ia-polemia');
  } catch {
    // Fallback for invalid URLs
    return url + (url.includes('?') ? '&' : '?') + 'ia-polemia';
  }
}


function getDisplayTitle(hit: HitResult): string {
  const rawTitle = hit.title ? removeEmojis(hit.title) : null;
  
  // If no title or generic title, try URL extraction
  if (!rawTitle || isGenericTitle(rawTitle, hit.url)) {
    const extracted = extractTitleFromUrl(hit.url);
    if (extracted) {
      // Apply French corrections (accents + hyphens)
      return fixFrenchTitle(extracted);
    }
  }
  
  return rawTitle || 'Document';
}

function getFaviconUrl(url: string): string {
  try {
    const domain = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
  } catch {
    return '';
  }
}

// Remove emojis from text
function removeEmojis(text: string): string {
  return text.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{231A}-\u{231B}]|[\u{23E9}-\u{23F3}]|[\u{23F8}-\u{23FA}]|[\u{25AA}-\u{25AB}]|[\u{25B6}]|[\u{25C0}]|[\u{25FB}-\u{25FE}]|[\u{2614}-\u{2615}]|[\u{2648}-\u{2653}]|[\u{267F}]|[\u{2693}]|[\u{26A1}]|[\u{26AA}-\u{26AB}]|[\u{26BD}-\u{26BE}]|[\u{26C4}-\u{26C5}]|[\u{26CE}]|[\u{26D4}]|[\u{26EA}]|[\u{26F2}-\u{26F3}]|[\u{26F5}]|[\u{26FA}]|[\u{26FD}]|[\u{2702}]|[\u{2705}]|[\u{2708}-\u{270D}]|[\u{270F}]|[\u{2712}]|[\u{2714}]|[\u{2716}]|[\u{271D}]|[\u{2721}]|[\u{2728}]|[\u{2733}-\u{2734}]|[\u{2744}]|[\u{2747}]|[\u{274C}]|[\u{274E}]|[\u{2753}-\u{2755}]|[\u{2757}]|[\u{2763}-\u{2764}]|[\u{2795}-\u{2797}]|[\u{27A1}]|[\u{27B0}]|[\u{27BF}]|[\u{2934}-\u{2935}]|[\u{2B05}-\u{2B07}]|[\u{2B1B}-\u{2B1C}]|[\u{2B50}]|[\u{2B55}]|[\u{3030}]|[\u{303D}]|[\u{3297}]|[\u{3299}]/gu, '').trim();
}

interface InlineQuote {
  quote: string;
  author: string;
  url: string;
}

interface InlineCitation {
  text: string;
  url: string;
}

interface ParsedAnswer {
  mainText: string;
  quotes: InlineQuote[];
  citations: InlineCitation[];
}

// Get source name from URL for citation display
function getSourceName(url: string): string {
  if (url.includes('citations.institut-iliade.com')) return 'Citatio';
  if (url.includes('archives.polemia.com')) return 'Archives Polémia';
  if (url.includes('ojim.fr')) return 'OJIM';
  if (url.includes('polemia.com')) return 'Polémia';
  if (url.includes('bernardlugan.blogspot.com')) return 'Bernard Lugan';
  if (url.includes('observatoire-justice.org')) return 'ODJ';
  if (url.includes('observatoire-immigration.fr')) return 'OID';
  if (url.includes('observatoire-violences.fr')) return 'Observatoire des violences politiques';
  if (url.includes('marc-vanguard.com')) return 'Marc Vanguard';
  if (url.includes('institut-iliade.com')) return 'Institut Iliade';
  if (url.includes('metapoinfos.hautetfort.com')) return 'Métapo Infos';
  if (url.includes('revue-elements.com')) return 'Éléments';
  if (url.includes('contribuablesassocies.org')) return 'Contribuables Associés';
  if (url.includes('institutpourlajustice.org')) return 'Institut pour la justice';
  if (url.includes('libertepolitique.com')) return 'Liberté politique';
  if (url.includes('ifrap.org')) return 'Fondation Ifrap';
  if (url.includes('maximetandonnet.wordpress.com')) return 'Maxime Tandonnet';
  if (url.includes('georgeslebreton.substack.com')) return 'Georges Le Breton';
  if (url.includes('lignedroite.substack.com')) return 'Ligne Droite';
  if (url.includes('islamisation.fr')) return "Observatoire de l'islamisation";
  return 'la source';
}

// Parse answer text to extract main text, inline quotes, and citations
function parseAnswerText(text: string): ParsedAnswer {
  if (!text || typeof text !== 'string') {
    return { mainText: '', quotes: [], citations: [] };
  }

  // Extract citations: lines starting with "- " followed by text and a link
  const citationPattern = /^- ([\s\S]*?)<a href='([^']+)'>Lire<\/a>$/gm;
  const citations = [...text.matchAll(citationPattern)].map((match) => ({
    text: match[1].trim(),
    url: match[2].trim(),
  }));

  let mainText = text;

  // Remove citation block (starts with "- " after newlines)
  const firstDashIndex = mainText.search(/\n-\s+[A-ZÀ-ÿ«"]/);
  if (firstDashIndex !== -1) {
    mainText = mainText.substring(0, firstDashIndex);
  }

  // Extract inline quotes: « quote » - Author <a href='url'>Lire</a>
  const quotesArray = [
    ...mainText.matchAll(
      /«\s*([^»]+)\s*»\s*-\s*([^<]+)<a href='([^']+)'>Lire<\/a>/g
    ),
  ];
  const quotes = quotesArray.map((m) => ({
    quote: m[1].trim(),
    author: m[2].trim(),
    url: m[3].trim(),
  }));

  // Remove inline quotes from main text
  mainText = mainText.replace(
    /^«\s*[\s\S]*?»\s*-\s*[^<]+<a href='[^']+'>Lire<\/a>\n?$/gm,
    ''
  );

  // Remove any remaining HTML links
  mainText = mainText.replace(/<a[^>]*>.*?<\/a>/gi, '');

  // Remove trailing source lines
  mainText = mainText.replace(
    /\n(Polémia|Archives Polémia|Institut Iliade|Citatio|OJIM|OID|ODJ|Marc Vanguard|Métapo Infos|Éléments|Contribuables Associés|Institut pour la justice|Liberté politique|TV Libertés|Fondation Ifrap|Maxime Tandonnet|Georges Le Breton|Ligne Droite),?\s*(None|\d{4})?.*/gi,
    ''
  );

  mainText = mainText.trim();

  return { mainText, quotes, citations };
}

// Deduplicate hits by URL
function deduplicateHits(hits: HitResult[]): HitResult[] {
  const seen = new Set<string>();
  return hits.filter(hit => {
    if (seen.has(hit.url)) return false;
    seen.add(hit.url);
    return true;
  });
}

// Fisher-Yates shuffle
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

const ResultCard: React.FC<ResultCardProps> = ({ answer, hits, actionType, cost, onRegenerate, question, isLoading }) => {
  const [copied, setCopied] = useState(false);
  
  // Trouver les livres partenaires correspondant à la question (max 4, aléatoires)
  const partnerHits = useMemo(() => {
    if (!question) return [];
    const matchingBooks = findMatchingBooks(question);
    const shuffled = shuffleArray(matchingBooks);
    return shuffled.slice(0, 4).map(book => ({
      url: book.url,
      title: book.title,
      isPartner: true as const,
      price: book.price,
      author: book.author,
    }));
  }, [question]);
  
  if (!answer && (!hits || hits.length === 0)) return null;

  const parsed = answer ? parseAnswerText(answer) : undefined;
  const cleanedAnswer = parsed?.mainText;
  const uniqueHits = hits ? deduplicateHits(hits) : [];

  const answerTitle = actionType === 'sumup' ? 'Résumé' : actionType === 'analyze' ? 'Analyse' : null;

  const handleCopy = async () => {
    if (!cleanedAnswer) return;
    try {
      await navigator.clipboard.writeText(cleanedAnswer);
      setCopied(true);
      toast({
        title: "Copié !",
        description: "La réponse a été copiée dans le presse-papiers.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Erreur",
        description: "Impossible de copier le texte.",
        variant: "destructive",
      });
    }
  };

  // Format cost in euros
  const formatCost = (costInDollars: number): string => {
    const euroCost = costInDollars * 0.85;
    const formatted = euroCost.toPrecision(3).replace('.', ',');
    return `${formatted}€`;
  };

  return (
    <div className="space-y-4 animate-fade-in-up">
      {cleanedAnswer && (
        <div className="bg-card p-4 sm:p-6 rounded-2xl shadow-lg border border-border/50 overflow-hidden">
          {answerTitle && (
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
              {answerTitle}
            </h3>
          )}
          <p
            className="text-foreground text-base leading-relaxed"
            dangerouslySetInnerHTML={{ __html: formatAnalysisText(cleanedAnswer).replace(/\n/g, '<br/>') }}
          />
          {isLoading && answer && (
            <div className="inline-flex items-center ml-2 align-middle">
              <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          )}

          {/* Inline quotes (Citatio style) */}
          {parsed?.quotes && parsed.quotes.length > 0 && (
            <div className="mt-4 space-y-3">
              {parsed.quotes.map((q, i) => (
                <div
                  key={i}
                  className="rounded-lg p-4 bg-muted/50 border border-border/40"
                >
                  <p className="text-foreground italic text-sm leading-relaxed">
                    «&nbsp;{q.quote}&nbsp;»
                  </p>
                  <p className="text-muted-foreground text-xs mt-2">{q.author}</p>
                  <a
                    href={q.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs mt-2 text-primary transition-colors hover:text-primary/80"
                  >
                    <img src={getFaviconUrl(q.url)} alt="" className="w-3 h-3 rounded-sm" />
                    Lire sur {getSourceName(q.url)}
                  </a>
                </div>
              ))}
            </div>
          )}

          {/* Inline citations */}
          {parsed?.citations && parsed.citations.length > 0 && (
            <div className="mt-4 space-y-3">
              {parsed.citations.map((c, i) => {
                const sourceName = getSourceName(c.url);
                return (
                  <div
                    key={i}
                    className="rounded-lg p-4 bg-muted/50 border border-border/40"
                  >
                    <p className="text-foreground text-sm leading-relaxed">{c.text}</p>
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs mt-2 text-primary transition-colors hover:text-primary/80"
                    >
                      <img src={getFaviconUrl(c.url)} alt="" className="w-3 h-3 rounded-sm" />
                      Lire sur {sourceName}
                    </a>
                  </div>
                );
              })}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-between items-center gap-2 mt-4 pt-4 border-t border-border/30">
            {/* Left side: Soutenir + Cost */}
            <div className="flex items-center gap-2">
              {cost !== undefined && (
                <>
                  <a
                    href="https://polemia.com/faireundon/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-[10px] px-2 py-1 bg-secondary/50 border border-border rounded text-muted-foreground sm:hover:bg-secondary sm:hover:text-foreground transition-colors"
                  >
                    <span className="text-red-500">♥</span> Soutenir
                  </a>
                  <span className="font-mono text-[10px] px-2 py-1 bg-secondary/50 border border-border rounded text-muted-foreground">
                    {formatCost(cost)}
                  </span>
                </>
              )}
            </div>
            {/* Right side: Actions */}
            <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors duration-150",
                copied 
                  ? "text-green-600 bg-green-50" 
                  : "text-muted-foreground sm:hover:text-primary sm:hover:bg-primary/10"
              )}
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span>Copié</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  <span>Copier</span>
                </>
              )}
            </button>
            </div>
          </div>
        </div>
      )}

      {(partnerHits.length > 0 || uniqueHits.length > 0) && (
        <div className="bg-card/80 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-border/50 space-y-4 overflow-hidden">
          
          {/* Liens partenaires */}
          {partnerHits.length > 0 && (
            <>
              <div className="mb-2">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Liens partenaires
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {partnerHits.map((hit, index) => {
                  const source = getSource(hit.url);
                  const faviconUrl = getFaviconUrl(hit.url);
                  const trackedUrl = addTrackingParam(hit.url);
                  return (
                    <a
                      key={`partner-${index}`}
                      href={trackedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "group flex items-center gap-4 p-5 rounded-xl border transition-all duration-200 sm:col-span-2",
                        "sm:hover:shadow-md sm:hover:-translate-y-0.5 cursor-pointer",
                        partnerCardStyle
                      )}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <p className="font-semibold text-base break-words">
                          {hit.title}
                        </p>
                        <p className="text-sm opacity-80 mt-1">
                          {hit.author} · {hit.price}
                        </p>
                        <div className="flex items-center gap-1.5 text-xs opacity-60 mt-2">
                          {faviconUrl && (
                            <img 
                              src={faviconUrl} 
                              alt="" 
                              className="w-3.5 h-3.5 rounded-sm"
                              loading="lazy"
                            />
                          )}
                          <span>{source}</span>
                        </div>
                      </div>
                      <ArrowRight className="w-5 h-5 shrink-0 opacity-50 sm:group-hover:opacity-100 sm:group-hover:translate-x-1 transition-all" />
                    </a>
                  );
                })}
              </div>
            </>
          )}

          {/* Sources classiques */}
          {uniqueHits.length > 0 && (
            <>
              <div className={cn(partnerHits.length > 0 && "mt-4", "mb-2")}>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Sources ({uniqueHits.length})
                </h3>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Pour aller plus loin dans votre lecture
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {uniqueHits.map((hit, index) => {
                  const source = getSource(hit.url);
                  const faviconUrl = getFaviconUrl(hit.url);
                  const isPdf = isPdfUrl(hit.url);
                  const trackedUrl = addTrackingParam(hit.url);
                  return (
                    <a
                      key={`source-${index}`}
                      href={trackedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "group flex items-center gap-3 p-4 rounded-xl border transition-all duration-200",
                        "sm:hover:shadow-md sm:hover:-translate-y-0.5 cursor-pointer",
                        cardStyle
                      )}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-center gap-2">
                          {isPdf && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-red-100 text-red-600 rounded shrink-0">
                              PDF
                            </span>
                          )}
                          <p className="font-medium truncate text-sm">
                            {isPdf ? getPdfFilename(hit.url) : getDisplayTitle(hit)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs opacity-70 mt-0.5">
                          {faviconUrl && (
                            <img 
                              src={faviconUrl} 
                              alt="" 
                              className="w-3.5 h-3.5 rounded-sm"
                              loading="lazy"
                            />
                          )}
                          <span>{source}</span>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 shrink-0 opacity-50 sm:group-hover:opacity-100 sm:group-hover:translate-x-1 transition-all" />
                    </a>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ResultCard;
